/**
 * Did this render error come from a script the browser failed to download?
 *
 * The search bar loads its heavier parts on demand (the results shelf arrives
 * mid-conversation). On a weak connection that download can fail, the render
 * throws, and the route's error page shows "Try again". Next's `reset()`
 * cannot recover from it: React caches a failed lazy import, so re-rendering
 * throws the very same error, every time. Only a real page load asks for the
 * script again.
 *
 * webpack names the error `ChunkLoadError` and words it "Loading chunk N
 * failed" (or "Loading CSS chunk N failed"); a failed native `import()` reads
 * "Failed to fetch dynamically imported module" (Chromium), "error loading
 * dynamically imported module" (Firefox) or "Importing a module script
 * failed" (Safari).
 */
export function isChunkLoadError(error: unknown): boolean {
  const { name, message } = (error ?? {}) as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;
  if (typeof message !== 'string') return false;
  return /loading (css )?chunk \S+ failed|dynamically imported module|importing a module script failed/i.test(
    message,
  );
}

/** What an error page's "Try again" does: reload for a lost script, else re-render. */
export function retryAfterRenderError(error: unknown, reset: () => void): void {
  if (isChunkLoadError(error) && typeof window !== 'undefined') {
    window.location.reload();
    return;
  }
  reset();
}
